import {
    AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, Output, EventEmitter, ViewContainerRef
} from '@angular/core';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {combineLatest, Subscription} from 'rxjs';
import {debounceTime, filter, take, takeWhile} from 'rxjs/operators';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {User} from '../../../model/user';
import {Preferences} from '../../../model/user-preferences.model';
import {NgcSearchService} from '@app/views/search/search.service';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {MatLegacyTabChangeEvent as MatTabChangeEvent} from '@angular/material/legacy-tabs';
import {
    getAuthenticatedUser,
    getAuthenticatedUserPreferences,
    getVendorInstitutions,
    isAuthenticated,
    State
} from '@app/app.reducers';
import {AuthenticatedUserRefreshTokenAction, SignOutAction} from '@app/views/common/auth.actions';
import {SchoolFilterType} from '../../../model/entity-profile';
import {AuthenticatedUserService} from '../../../service/authenticated-user.service';
import {UserActivityService} from '@app/core/global/user-activity.service';
import {InteractiveGuideService} from '@app/core/service/interactive-guide.service';
import {getAuthenticatedUserApplications} from '@app/app.reducers';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import * as integrationActions from '@app/core/reducers/integration/integration.actions';
import {InstitutionsVendorListModel} from '@app/core/model/institutional-model';
import {AuthenticationService} from '@app/core/service/authentication.service';
import {
    MatLegacyDialog as MatDialog,
    MatLegacyDialogConfig as MatDialogConfig,
    MatLegacyDialogRef as MatDialogRef
} from '@angular/material/legacy-dialog';
import {ConfirmDialogComponent} from '@app/core/comp/dialog/confirm-dialog/confirm-dialog.component';
import {DataBrowser} from '@app/views/databrowsing/data-browser';

@Component({
    selector: 'ngc-toolbar-glob',
    templateUrl: './toolbar-glob.component.html',
    styleUrls: ['./toolbar-glob.component.scss']
})
export class ToolbarGlobComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input()
    set helpCenterAvailable(val: boolean) {
        this._helpCenterAvailable = val;
    }

    get helpCenterAvailable(): boolean {
        return this._helpCenterAvailable;
    }

    @Input()
        focused = false;

    @Output()
        onToggleMenu = new EventEmitter<any>();

    @Output()
        onOpenHelpCenter = new EventEmitter<any>();

    @ViewChild('searchTerm')
        searchTermInput: ElementRef;

    public userPreferences: Preferences;
    public vendorApplications: Application[];
    public currentApplication: Application;

    public currentPageTourName = '';
    public toursAllowed = false;
    public isToursPermissionSaving = false;

    private _confirmUserActivityDialog: MatDialogRef<ConfirmDialogComponent>;
    private _helpCenterAvailable: boolean;
    private _useReturnUrlOnLogout = false;

    placeholderText: string;
    colorTheme: string;
    user: User;

    contextDistrict: any;
    // searchInDistrict = false;

    searchForm: UntypedFormGroup;

    searchObj: any = {
        term: null,
        district: null
    };

    selectedTabIndex: any;

    private filterIds: SchoolFilterType[] = [
        SchoolFilterType.ALL,
        SchoolFilterType.ACTIVE,
        SchoolFilterType.PENDING,
        SchoolFilterType.INACTIVE,
        SchoolFilterType.WITHERROR
    ];

    private subscriptions: Subscription[] = [];
    private alive = true;
    readonly CREATE_APP_ROUTE = '/admin/applications/add';

    constructor(
        public router: Router,
        private location: Location,
        public authenticatedUserService: AuthenticatedUserService,
        public searchService: NgcSearchService,
        private formBuilder: UntypedFormBuilder,
        private viewContainerRef: ViewContainerRef,
        private matDialog: MatDialog,
        private store: Store<State>,
        private userActivityService: UserActivityService,
        private interactiveGuideService: InteractiveGuideService,
        private snackBar: MatSnackBar,
        private dataBrowser: DataBrowser,
        private authenticationService: AuthenticationService
    ) {

        this.searchForm = this.formBuilder.group({
            searchTerm: [''],
            searchInDistrict: [false]
        });

        this.setSearchContent(this.searchService.searchContext);

        this.subscriptions.push(
            combineLatest(
                this.store.pipe(select(getAuthenticatedUserPreferences)),
                this.store.pipe(select(getAuthenticatedUser)),
                this.store.pipe(select(getAuthenticatedUserApplications))
            )
                .pipe(takeWhile(() => this.alive))
                .subscribe(([prefs, user, apps]: [Preferences, User, Application[]]) => {
                    this.userPreferences = prefs;

                    this.user = user;
                    this.placeholderText = this.getSearchPlaceholder(user);

                    if (this.authenticatedUserService.isVendor(user) && apps !== undefined) {
                        apps.sort((app1: Application, app2: Application) => app1.name.localeCompare(app2.name));
                        this.vendorApplications = apps;
                        this.currentApplication = apps.find((app: Application) => this.userPreferences
                                && (app.guid === this.userPreferences.getPreferencesItemValue('currentApp'))) || apps[0];
                    }

                    this.toursAllowed = this.interactiveGuideService.isInteractiveTourAllowed();
                    if (this.isToursPermissionSaving) {
                        this.isToursPermissionSaving = this.interactiveGuideService.isInteractiveTourAllowed() !== this.toursAllowed;
                    }
                })
        );

        // subscribe to logout success
        this.subscriptions.push(
            this.store.pipe(
                select(isAuthenticated),
                takeWhile(() => this.alive)
            ).subscribe((authenticated: boolean) => {
                if (!authenticated) {
                    this.matDialog.closeAll();
                    setTimeout(() => {
                        // const navExtras = this._useReturnUrlOnLogout ? {queryParams: {lastVisitedPage: this.router.routerState.snapshot.url}} : undefined;
                        // this.authenticationService.redirectToLoginPage(navExtras);
                        this._useReturnUrlOnLogout = false;
                    }, 0);
                }

                if (authenticated) {
                    this.userActivityService.startActivitySyncAcrossTabs();
                } else {
                    this.userActivityService.stopActivitySyncAcrossTabs();
                }
            })
        );

        this.subscriptions.push(
            this.searchService.onSearchContextChanged
                .subscribe(context => {
                    this.setSearchContent(context);
                }));

        this.subscriptions.push(
            this.interactiveGuideService.currentPageTour$
                .pipe(filter(tourName => tourName != 'welcomeTour'))
                .subscribe(tour => {
                    setTimeout(
                        () => this.currentPageTourName = tour,
                        0
                    );
                })
        );

        this.subscriptions.push(
            this.userActivityService.onUserInactive$.subscribe(() => {
                this.matDialog.closeAll();
                this._useReturnUrlOnLogout = true;
                localStorage.setItem('lastAuthenticatedUser', this.user ? this.user.username : 'null');
                localStorage.setItem('lastAuthenticatedRole', this.user ? this.user.role : 'null');
                this.store.dispatch(new SignOutAction());
                this.snackBar.open('Logout is caused by no user activity', '', <MatSnackBarConfig>{duration: 3000});
            }),
            this.userActivityService.promptUserActivityConfirmation$.subscribe((showDialog: boolean) => {
                if (showDialog && !this._confirmUserActivityDialog) {
                    const config = new MatDialogConfig();
                    config.viewContainerRef = this.viewContainerRef;
                    config.autoFocus = false;
                    config.width = '620px';
                    config.disableClose = true;
                    this._confirmUserActivityDialog = this.matDialog.open(ConfirmDialogComponent, config);
                    this._confirmUserActivityDialog.componentInstance.item = 'other';
                    this._confirmUserActivityDialog.componentInstance.header = 'Are you still here?';
                    this._confirmUserActivityDialog.componentInstance.target = `Your session is about to expire due to inactivity. Press 'Yes' to stay signed in. Otherwise, you will be logged out automatically `;
                    this._confirmUserActivityDialog.componentInstance.otherItemCancelBtnTitle = 'No';
                    this._confirmUserActivityDialog.componentInstance.otherItemConfirmBtnTitle = 'Yes';

                    this._confirmUserActivityDialog.afterClosed()
                        .pipe(
                            take(1),
                            takeWhile(() => this.alive)
                        )
                        .subscribe(result => {
                            if (result === 'ok') {
                                this.userActivityService.refreshInactivityTimeout();
                                this.store.dispatch(new AuthenticatedUserRefreshTokenAction());
                            } else if (result === '') {
                                this.clickLogout();
                            }
                        });
                } else if (!showDialog) {
                    this._confirmUserActivityDialog?.close();
                    this._confirmUserActivityDialog = null;
                }
            })
        );
    }

    // @HostListener('window:mousemove') refreshUserActivityState() {
    //     this.userActivityService.refreshInactivityTimeout();
    // }

    ngOnInit() {
        this.colorTheme = this.focused ? 'white' : 'primary';
        this.placeholderText = this.getSearchPlaceholder();
        this.toursAllowed = this.interactiveGuideService.isInteractiveTourAllowed();

        this.subscriptions.push(
            this.searchForm.get('searchTerm').valueChanges
                .subscribe(newValue => {
                    this.searchObj.term = newValue;
                }));
        this.subscriptions.push(
            this.searchForm.get('searchTerm').valueChanges
                .pipe(debounceTime(1000))
                .subscribe(newValue => {
                    this.doSearch();
                }));
        this.subscriptions.push(
            this.searchForm.get('searchInDistrict').valueChanges
                .subscribe(newValue => {
                    this.doSearch();
                }));
    }

    private setSearchContent(context: any) {
        this.contextDistrict = context.district;
        this.searchForm.get('searchInDistrict').setValue(!!this.contextDistrict);
        this.placeholderText = this.getSearchPlaceholder();

        this.selectedTabIndex = this.getIndexByFilterIdIndex(context.filter);
    }

    doSearch() {
        this.searchObj.district = this.searchForm.get('searchInDistrict').value ? this.contextDistrict : null;
        this.searchService.onSearchTermChanged.emit(this.searchObj);
    }

    ngAfterViewInit(): void {
        if (this.focused) {
            this.searchTermInput.nativeElement.focus();
        }
    }

    navigateBack() {
        this.location.back();
    }

    navigateHome() {
        this.router.navigate(['/']);
    }

    clickSearch(event) {
        this.router.navigate(['/search']);
    }

    toggleMenu() {
        this.onToggleMenu.emit();
    }

    openHelpCenter(): void {
        this.onOpenHelpCenter.emit();
    }

    startCurrentPageTour(): void {
        setTimeout(() => this.interactiveGuideService.startCurrentPageTour(this.currentPageTourName), 0);
    }

    changeToursPermit(): void {
        this.toursAllowed = !this.toursAllowed;
        this.interactiveGuideService.toggleToursPermit(this.toursAllowed);
        this.isToursPermissionSaving = true;
    }

    clickProfile() {
        this.router.navigate(['/user/profile']);
    }

    clickApp(application: Application) {
        const currentApp = this.userPreferences.getPreferencesItem('currentApp');
        const updateUserPreferences  = !(currentApp && ((currentApp.selectedId === application.guid) && (currentApp.selectedFriendlyName === application.name)));
        if (updateUserPreferences) {

            // when user is on integration settings page (applications/edit/:appGuid/:institutionGuid) and changes current app,
            // than navigate to institutions page
            this.navigateToVendorInstitutionsWhenNeeded(currentApp.selectedId);

            this.store.dispatch(new integrationActions.UpdateVendorCurrentAppAction({currentAppId: application.guid}));

        } else {
            if (this.router.url === this.CREATE_APP_ROUTE) {
                this.router.navigate(['/admin/applications/myapplication/edit']);
            }
        }
    }

    private navigateToVendorInstitutionsWhenNeeded(applicationGuid: string): void {
        this.subscriptions.push(
            this.store.pipe(
                select(getVendorInstitutions),
                take(1)
            )
                .subscribe((institutions: InstitutionsVendorListModel[]) => {
                    if (institutions && (institutions.length > 0)) {
                        const navigateToInstitutionsPage: boolean = this.router.url.includes(`/admin/sharedpool`)
                            || institutions.some((institution: InstitutionsVendorListModel) =>
                                this.router.url.includes(`/admin/applications/edit/${applicationGuid}/${institution.institutionGuid}`));
                        if (navigateToInstitutionsPage) {
                            this.router.navigate(['/admin/institutions']);
                        }
                    }
                }));
    }

    clickAddApp() {
        this.router.navigate(['/admin/applications/add']);
    }

    clickLogout() {
        localStorage.setItem('lastAuthenticatedUser', this.user ? this.user.username : 'null');
        localStorage.setItem('lastAuthenticatedRole', this.user ? this.user.role : 'null');
        this.store.dispatch(new SignOutAction());
    }

    onTabChangeClick(event: MatTabChangeEvent) {
        const filterId = this.getFilterIdByIndex(event.index);
        this.searchService.setContextFilter(filterId);
    }

    resetContextDistrict() {
        this.searchService.setContextDistrict(null);
    }

    private getFilterIdByIndex(index: number): string {
        // 0 - ALL
        // 1 - RECENTLY APPROVED
        // 2 - PENDING APPROVAL
        // 3 - WITHOUT DATA SYNC
        // 4 - WITH VALIDATION ERROR

        return this.filterIds[index].toString();
    }

    private getIndexByFilterIdIndex(filterId): number {
        return this.filterIds.indexOf(filterId);
    }

    private getSearchPlaceholder(user?: any) {
        if (this.authenticatedUserService.isVendor(user)) {
            return this.searchForm.get('searchInDistrict').value ? 'Search School' : 'Search District or School';
        } else if (this.authenticatedUserService.isProvider(user)) {
            return 'Search Application';
        } else {
            return 'Search';
        }
    }

    public ngOnDestroy() {
        this.userActivityService.stopActivitySyncAcrossTabs();
        this.matDialog.closeAll();
        this.alive = false;
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
